import { Component, OnInit, SecurityContext, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { startWith, debounceTime, distinctUntilChanged, tap, firstValueFrom } from 'rxjs';
import { ChargingUser, UpdateChargingUserAddress, UpdateIdTagInformationRequest } from '../charging-user.model';
import { DomSanitizer } from '@angular/platform-browser';
import { AddressService } from 'src/app/core/services/address.service';
import { AddressLookupService } from 'src/app/shared/services/address-lookup.service';
import { SnackbarService } from 'ok-angular-shared/components';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { ChargingUserService } from 'src/app/shared/services/charging-user.service';
import { Address } from 'src/app/shared/models/address';
import { IdTagUserType } from 'src/app/shared/enums/id-tag-user-type.enum';
import { CustomDropDownArrowDirective } from 'ok-angular-shared/directives';
import { MatDialogModule } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatOptionModule } from '@angular/material/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatRadioModule } from '@angular/material/radio';
import { AdmG4AccountNumberComponent } from 'src/app/shared/components/adm-g4-account-number/adm-g4-account-number.component';
import { AdmG4AgreementNumberComponent } from 'src/app/shared/components/adm-g4-agreement-number/adm-g4-agreement-number.component';

interface ChargingUserAddIdTagInfolModel {
  deliveryAddress: FormControl<string | null>;
  userHaveOrganizationCar: FormControl<boolean | null>;
  addressLookup: FormControl<string>;
  agreementNumber: FormControl<number | null>;
  invoicingAccountNumber: FormControl<number | null>;
}

@Component({
  selector: 'adm-charging-user-add-id-tag-info-modal',
  templateUrl: './charging-user-add-id-tag-info-modal.component.html',
  styleUrls: ['./charging-user-add-id-tag-info-modal.component.css'],
  imports: [
    ReactiveFormsModule,
    MatDialogModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatOptionModule,
    MatButtonModule,
    MatRadioModule,
    MatIconModule,
    MatCheckboxModule,
    CustomDropDownArrowDirective,
    AdmG4AccountNumberComponent,
    AdmG4AgreementNumberComponent,
  ],
})
export class ChargingUserAddIdtaginfoModalComponent implements OnInit {
  data = inject<{
    chargingUser;
  }>(MAT_DIALOG_DATA);
  dialogRef = inject<MatDialogRef<ChargingUserAddIdtaginfoModalComponent>>(MatDialogRef);
  private chargingUserService = inject(ChargingUserService);
  private snackbarService = inject(SnackbarService);
  private sanitizer = inject(DomSanitizer);
  private addressService = inject(AddressService);
  private addressLookupService = inject(AddressLookupService);

  public addressFoundAndSelected = false;
  public chargingUser: ChargingUser;
  public filteredAddresses: UpdateChargingUserAddress[] = [];
  public userFormGroup: FormGroup<ChargingUserAddIdTagInfolModel> = new FormGroup<ChargingUserAddIdTagInfolModel>({
    deliveryAddress: new FormControl<string | null>(null),
    userHaveOrganizationCar: new FormControl<boolean | null>(false),
    addressLookup: new FormControl<string>('', { validators: [Validators.required], nonNullable: true }),
    invoicingAccountNumber: new FormControl<number | null>(null),
    agreementNumber: new FormControl<number | null>(null),
  });

  ngOnInit() {
    this.chargingUser = this.data.chargingUser;
    this.userFormGroup.controls.userHaveOrganizationCar.valueChanges.subscribe(val => {
      if (!val) {
        this.userFormGroup.controls.agreementNumber.disable();
        this.userFormGroup.controls.invoicingAccountNumber.enable();
      }
      if (val) {
        this.userFormGroup.controls.invoicingAccountNumber.disable();
        this.userFormGroup.controls.agreementNumber.enable();
      }
    });
    this.onAddressFormChanges();
  }

  public emptyAddress() {
    if (!this.addressFoundAndSelected) {
      this.userFormGroup.controls.addressLookup.setValue('');
      this.userFormGroup.controls.deliveryAddress.setValue(null);
    }
  }

  private onAddressFormChanges() {
    this.userFormGroup.controls.addressLookup.valueChanges
      .pipe(
        startWith(''), // let one blank through to search for all
        debounceTime(300),
        distinctUntilChanged(),
        tap(searchTerm => this.sanitizer.sanitize(SecurityContext.URL, searchTerm))
      )
      .subscribe(async val => {
        if (val && !this.addressFoundAndSelected) {
          this.userFormGroup.controls.deliveryAddress.setValue(null);
          this.filteredAddresses = await firstValueFrom(this.addressLookupService.lookup(val));
        } else if (val === '') {
          this.userFormGroup.controls.deliveryAddress.setValue(null);
          this.addressFoundAndSelected = false;
        }
      });
  }

  public selectAddress(address: Address) {
    this.addressFoundAndSelected = true;
    const addressFormatted = this.formatAddressToString(address);
    this.userFormGroup.controls.addressLookup.setValue(addressFormatted);
    this.userFormGroup.controls.deliveryAddress.setValue(addressFormatted);
  }
  public formatAddressToString(address: UpdateChargingUserAddress): string {
    return this.addressService.formatAddressToString(address as Address);
  }

  public save() {
    const hasCompanyCar = this.userFormGroup.controls.userHaveOrganizationCar.value;
    const request: UpdateIdTagInformationRequest = {
      idTagUserType: hasCompanyCar ? IdTagUserType.CompanyCar : IdTagUserType.NoCompanyCar,
      invoicingAccountNumber: !hasCompanyCar ? this.userFormGroup.controls.invoicingAccountNumber.value : null,
      agreementNumber: hasCompanyCar ? this.userFormGroup.controls.agreementNumber.value : null,
      shippingAddress: this.userFormGroup.controls.deliveryAddress.value,
    };
    this.chargingUserService.updateIdTagInformation(this.chargingUser.id, request).subscribe({
      next: () => {
        this.snackbarService.success('Brugeren er opdateret og kan nu tilknyttes brikker');
        this.dialogRef.close();
      },
    });
  }
}
